import {
    all, call, cancelled, fork, put, select, take, takeEvery, takeLatest,
} from 'redux-saga/effects';
import { LOCATION_CHANGE, LocationChangeAction, RouterState } from 'connected-react-router';
import { ActionType } from 'typesafe-actions';
import {
    setPrintMode,
    toggleFocusMode,
    workspaceSchemeError,
    workspaceSchemeLoaded,
} from 'common/store/workspaces/actions';
import { WorkspaceTypeKeys } from 'common/store/workspaces/types';
import { TypeKeys as DetailsPanelTypeKeys } from 'common/features/detailsPanel/actions';
import { WorkspacesState } from 'common/store/workspaces/reducer';
import { WorkspaceGroup } from 'common/store/common/workspaceScheme/types';
import { refreshMissingProperties, replaceContext } from 'common/store/context/actions';
import commonWorkspaceSchemeService from 'common/services/commonWorkspaceSchemeService';
import { SessionState } from 'common/store/session/reducer';
import { pushAlert } from 'common/components/notifications/actions';
import { Alert, AlertLevel } from 'common/components/notifications/types';
import { getUrlParam } from 'common/util/url/urlUtil';
import { propsAreIdentical } from 'common/util/object/objectUtil';
import commonSearchQueryBuilder from 'common/services/search/commonSearchQueryBuilder';
import { HitData, SearchFilters, SearchQuery } from 'common/store/common/search/types';
import commonApiService from 'common/services/api/commonApiService';
import { isNullOrUndefined } from 'common/util/utils';
import { Context, workspaceID } from 'common/store/common/constants';
import { delay } from 'redux-saga';
import { AssessmentRunSessionStatus, ContextState } from './reducer';
import { ContextTypeKeys, ContextUrlSearchStringObject } from './types';
import { ContextTypeKeys as AssessmentKeys } from '../assessment/actions';
import routingService from '../../services/routingService';
import {
    assessmentRunIdUpdated,
    assessmentRunSessionLoaded,
    committeeIdSelected,
    contextPropertiesUpdated,
    openExplanationRecordingPanelDone,
    partyIdUpdated,
    questionIdUpdated, refreshHeartbeat,
    setGradingWorkflowInfo,
    setPartyState,
    setQuestionState,
    speakingQuestionSetIdsLoaded,
    toggleDetailsPanel,
    updateQuestionState,
    workspaceIdUpdated,
} from './actions';
import apiService from '../../services/api/apiService';
import { GRADING_PARTY_INDEX, USERQUESTION_INDEX } from '../common/search/constants';
import { AssessmentState } from '../assessment/reducer';
import urlContextParametersService from '../../services/urlContextParametersService';
import { RootState } from '../rootReducer';
import { GradingPartyDocument } from '../../services/gradingPartyService';
import { RecordingTypeKeys } from '../../features/recordingHeader/types';
import searchQueryBuilderService from '../../services/search/searchQueryBuilderService';
import { getVisibleWorkspaces, getWorkspaceIdByWorkflowKey } from '../../services/workspaceSchemeService';
import { getContext } from '../assessment/sagas';
import { parseSpeakingQuestionSetIds } from '../../services/speakingQuestionSetService';
import { getArSessionCandidateContributor } from '../../services/assessmentRunSessionService';
import { showReload } from '../../components/reload/actions';
import navigationListService from '../../services/navigationListService';
import { openAiSupportedMarkingIntro } from '../../features/aiSupportedMarkingIntro/actions';
import { shouldOpenAiIntro } from '../../features/aiSupportedMarkingIntro/guard';
import { isAiIntroKnownReadOnly } from '../../features/aiSupportedMarkingIntro/introReadOnly';
import { getLoggedInUserId } from '../../features/aiSupportedMarkingIntro/dontShowAgainPreference';

type RefreshPropertiesType = ActionType<typeof refreshMissingProperties>;

export const getContextState = (state: RootState) => state.context;
export const getRouterState = (state: RootState) => state.router;
export const getWorkspaceState = (state: RootState) => state.workspaces;
export const getSessionState = (state: RootState) => state.session;
export const getAssessmentState = (state: RootState) => state.assessment;

const propertiesRequestedMap: { [key: number]: { [key: string]: boolean } } = {};

const propertyIsRequested = (assessmentRunId: number, propertyName: string) => {
    return propertiesRequestedMap[assessmentRunId]
        && propertiesRequestedMap[assessmentRunId][propertyName] !== undefined;
};

const setPropertyAsRequested = (assessmentRunId: number, propertyName: string) => {
    const propsForAssessmentRunId = propertiesRequestedMap[assessmentRunId] || {};
    propsForAssessmentRunId[propertyName] = true;
    propertiesRequestedMap[assessmentRunId] = propsForAssessmentRunId;
};

const partyIndexProperties = ['partyId', 'partyState'];
const questionIndexProperties = ['questionId', 'questionState'];
const assessmentRunSessionProperties = ['assessmentRunSession'];
const speakingQuestionSetIdsProperties = ['speakingQuestionSetIds'];

const isContextNotInSyncWithUrl = (contextState: ContextState, newContext: ContextUrlSearchStringObject) => {
    return !propsAreIdentical(
        ['assessmentRunId', 'partyId', 'questionId', 'selectedCommitteeId', 'workspaceId'],
        contextState,
        newContext,
    );
};

function* setQuestionStateData(pQuestionId?: any) {
    const session: SessionState = yield select(getSessionState);
    const contextState: ContextState = yield select(getContextState);
    const { assessmentRunId } = contextState;
    const isStudent = session.user.isStudent || false;
    const questionFilters: SearchFilters = {
        assessmentRunId,
        partyId: contextState.partyId,
    };
    const questionId = pQuestionId || contextState?.questionId;

    if (questionId && questionId > 0) {
        questionFilters.questionId = questionId;
    } else if (contextState.questionId > 0) {
        questionFilters.questionId = contextState.questionId;
    }

    let questionInfo: SearchQuery = commonSearchQueryBuilder.createQueryWithFilterAndSort(
        questionFilters,
        'questionNumber',
        'asc',
        1,
    );
    questionInfo = commonSearchQueryBuilder.appendSource(questionInfo, 'context');
    const questionSearchJSON: string = encodeURIComponent(JSON.stringify(questionInfo));
    let dataQuestion = null;

    if (isStudent) {
        dataQuestion = yield call(
            apiService.fetchListStudentQuestions, contextState.assessmentRunId, questionSearchJSON
        );
    } else {
        dataQuestion = yield call(commonApiService.fetchListLiveData, USERQUESTION_INDEX, questionSearchJSON);
    }

    if (dataQuestion.hits && dataQuestion.hits.length > 0) {
        const {
            questionNumber, questionNumbering, questionType,
            documentContentRevisionId, questionName, answered, score, evaluatorScores, maxScore,
            questionWeight, rubricType, stimulusContentRevisionId, hasStimulusUserResponse, hasMaximaValidationIssue,
        } = dataQuestion.hits[0];

        if (contextState.questionId === -1) {
            urlContextParametersService.setQuestionId(dataQuestion.hits[0].questionId);
        }
        yield put(setQuestionState({
            questionNumber,
            questionNumbering,
            questionType,
            documentContentRevisionId,
            questionName,
            answered,
            score,
            evaluatorScores,
            maxScore,
            questionWeight,
            rubricType,
            hasStimulusUserResponse,
            stimulusContentRevisionId,
            noQuestionFound: false,
            hasMaximaValidationIssue,
        }));
    } else {
        // reset state
        yield put(setQuestionState({
            documentContentRevisionId: -1,
            questionType: '',
            questionNumber: -1,
            questionNumbering: '',
            questionName: '',
            answered: false,
            score: undefined,
            evaluatorScores: undefined,
            maxScore: undefined,
            questionWeight: 1,
            rubricType: undefined,
            stimulusContentRevisionId: -1,
            hasStimulusUserResponse: false,
            noQuestionFound: true,
            hasMaximaValidationIssue: false,
        }));
    }
}

function* loadFirstUserQuestionForParty(assessmentRunId: number, requiredProps: string[]) {
    if (propertyIsRequested(assessmentRunId, 'questionId')) {
        return;
    }
    setPropertyAsRequested(assessmentRunId, 'questionId');

    if (requiredProps.includes('questionState')) {
        yield put(updateQuestionState());
    }
}

function* setPartyStateData(partyId?: number, forceSetPartyId?: boolean) {
    const sessionState: SessionState = yield select(getSessionState);
    if (sessionState.user.isStudent) {
        return;
    }
    const contextState: ContextState = yield select(getContextState);
    const { assessmentRunId } = contextState;
    const partyFilters: SearchFilters = { assessmentRunId };
    if (partyId && partyId > 0) {
        partyFilters.partyId = partyId;
    } else if (contextState.partyId > 0 && !forceSetPartyId) {
        partyFilters.partyId = contextState.partyId;
    }

    let partyInfo: SearchQuery = commonSearchQueryBuilder.createQueryWithFilterAndSort(
        partyFilters,
        'partyName',
        'asc',
        1,
    );
    partyInfo = commonSearchQueryBuilder.appendSource(partyInfo, 'context');
    partyInfo = searchQueryBuilderService.appendSubmissionStatusFilter(partyInfo);
    const partyInfoSearchJSON: string = encodeURIComponent(JSON.stringify(partyInfo));
    const data = yield call(commonApiService.fetchListLiveData, GRADING_PARTY_INDEX, partyInfoSearchJSON);
    if (data.hits && data.hits.length) {
        const gradingPartyDocument: GradingPartyDocument = data.hits[0];
        const {
            partyId, partyName, committeeId,
        } = gradingPartyDocument;
        if (contextState.partyId === -1 || forceSetPartyId) {
            urlContextParametersService.setPartyId(partyId);
        }
        yield put(setPartyState({
            partyName,
            partyCommitteeId: committeeId,
            partyId,
            gradingPartyDocument,
        }));
    }
}


function* loadFirstPartyForAssessmentRun(assessmentRunId: number) {
    const session: SessionState = yield select(getSessionState);
    const assessment: AssessmentState = yield select(getAssessmentState);
    const contextState: ContextState = yield select(getContextState);
    const isStudent = session.user.isStudent;
    if (propertyIsRequested(assessmentRunId, 'partyId')) {
        return;
    }
    setPropertyAsRequested(assessmentRunId, 'partyId');

    if (isStudent) {
        if (contextState.partyId <= 0) {
            const { deliveringPartyId } = assessment;
            if (deliveringPartyId > 0) {
                urlContextParametersService.setPartyId(deliveringPartyId);
            }
        }
        return;
    }

    yield setPartyStateData();
}

function* loadPartyNameForStudent() {
    const { assessmentRunId }: ContextState = yield select(getContextState);
    const session: SessionState = yield select(getSessionState);
    try {
        const data = yield call(apiService.fetchTest, assessmentRunId);
        const { candidateSubmissionAccessLevel } = data.items[0];
        const { candidateExternalId } = data.items[0];
        // Store candidateSubmissionAccessLevel in context.candidateSubmissionAccessLevel (not in partyState)
        yield put({ type: 'SET_CANDIDATE_SUBMISSION_ACCESS_LEVEL', payload: candidateSubmissionAccessLevel });
        yield put(setPartyState({
            partyName: candidateExternalId,
            partyId: session.user.id,
        }));
    } catch (error) {
        yield put({ error, type: 'LOAD_STUDENT_PARTY_NAME_FAILED' });
    }
}

function* loadMissingProperties(action: any) {
    const session: SessionState = yield select(getSessionState);
    const { assessmentRunId }: ContextState = yield select(getContextState);
    const isStudent = session.user.isStudent || false;
    try {
        if (action.payload.some((prop: string) => partyIndexProperties.includes(prop))) {
            yield loadFirstPartyForAssessmentRun(assessmentRunId);
        }

        if (action.payload.some((prop: string) => questionIndexProperties.includes(prop))) {
            const contextState: ContextState = yield select(getContextState);
            if (contextState.partyId > 0) {
                yield loadFirstUserQuestionForParty(assessmentRunId, action.payload);
            }
        }

        if (action.payload.some((prop: string) => assessmentRunSessionProperties.includes(prop))) {
            const contextState: ContextState = yield select(getContextState);
            if (contextState.assessmentRunId > 0) {
                yield callLoadMissingAssessmentRunSession(contextState.assessmentRunId);
            }
        }

        if (action.payload.some((prop: string) => speakingQuestionSetIdsProperties.includes(prop))) {
            const contextState: ContextState = yield select(getContextState);
            const { assessmentRunSession, assessmentRunId } = contextState;
            if (assessmentRunSession?.assessmentRunSessionId > 0) {
                yield callLoadMissingSpeakingQuestionSetIds(assessmentRunId);
            }
        }
        if (isStudent && action.payload.some((prop: string) => prop === 'partyState')) {
            yield loadPartyNameForStudent();
        }
    } catch (error) {
        yield put({ error, type: 'UPDATE_PARTY_AND_QUESTIONS_FAILED' });
    }
}

function* callLoadMissingAssessmentRunSession(assessmentRunId: number) {
    if (propertyIsRequested(assessmentRunId, 'assessmentRunSession')) {
        return;
    }
    setPropertyAsRequested(assessmentRunId, 'assessmentRunSession');

    yield callFetchAssessmentRunSessionInfo();
}

function* callLoadMissingSpeakingQuestionSetIds(assessmentRunId: number) {
    if (propertyIsRequested(assessmentRunId, 'speakingQuestionSetIds')) {
        return;
    }
    setPropertyAsRequested(assessmentRunId, 'speakingQuestionSetIds');

    yield callFetchSpeakingQuestionSetIds();
}

function* updateMissingProperties(action: RefreshPropertiesType) {
    const session: SessionState = yield select(getSessionState);
    const isStudent = session.user.isStudent || false;
    const { payload: { missingProperties, id } } = action;
    try {
        if (missingProperties.some((prop: string) => partyIndexProperties.includes(prop))) {
            yield setPartyStateData(id);
        }
        if (missingProperties.some((prop: string) => questionIndexProperties.includes(prop))) {
            const contextState: ContextState = yield select(getContextState);
            if (contextState.partyId > 0) {
                yield call(setQuestionStateData, id);
            }
        }
        if (isStudent && missingProperties.some((prop: string) => prop === 'partyState')) {
            yield loadPartyNameForStudent();
        }

        yield put(contextPropertiesUpdated());
    } catch (error) {
        yield put({ error, type: 'UPDATE_PARTY_AND_QUESTIONS_FAILED' });
    }
}

function* setLastVisitUrlToLocalStorage() {
    const sessionState: SessionState = yield select(getSessionState);

    if (sessionState.user.id) {
        const storageKey = routingService.getStorageKeyForLastVisitedUrl();

        yield localStorage.setItem(storageKey, `/${window.location.search}`);
    }
}

function* syncQueryParametersWithState() {
    const workspaceState: WorkspacesState = yield select(getWorkspaceState);
    const focusView: string = getUrlParam('focusView');
    const printMode: string = getUrlParam('printMode');
    if (focusView === 'true' && !workspaceState.focusMode) {
        yield put(toggleFocusMode());
    }
    if (printMode === 'true' && !workspaceState.printMode) {
        yield put(setPrintMode(true));
    }

    // grading is not loaded in an iframe
    if (window === window.parent && !printMode) {
        yield call(setLastVisitUrlToLocalStorage);
    }
}

function* loadWorkspaceScheme() {
    const contextState: ContextState = yield select(getContextState);
    const { assessmentRunId, assessmentRunSession, workspaceId } = contextState;
    if (assessmentRunId < 0) {
        return;
    }

    try {
        const data = yield call(
            apiService.fetchGradingWorkspaces,
            assessmentRunId,
        );

        const previewScheme = getUrlParam('previewScheme');
        if (previewScheme) {
            data.workspaceScheme.workspaceGroups.forEach((workspaceGroup: WorkspaceGroup) => {
                const index = workspaceGroup.workspaces
                    .findIndex((workspace) => commonWorkspaceSchemeService.getWorkspaceIdentifier(workspace) === 'plan_workspace_visibility');
                if (index > -1) {
                    workspaceGroup.workspaces.splice(index, 1);
                }
            });
        }
        if (data.workspaceScheme) {
            yield put(workspaceSchemeLoaded(data.workspaceScheme));
        }
        const workspacesState: WorkspacesState = yield select(getWorkspaceState);
        const { workspaceScheme } = workspacesState;
        const { workspaceGroups } = workspaceScheme;
        const hasWorkspaceId = workspaceId && workspaceId !== '-';

        if (!hasWorkspaceId && workspaceGroups?.length > 0) {
            let workspaceIdForUrl;

            if (assessmentRunSession?.workflowKey) {
                workspaceIdForUrl = getWorkspaceIdByWorkflowKey(workspaceScheme, assessmentRunSession.workflowKey);
            } else {
                const firstWorkspace = commonWorkspaceSchemeService.getFirstVisibleWorkspace(
                    workspaceGroups,
                );
                if (firstWorkspace) {
                    workspaceIdForUrl = commonWorkspaceSchemeService.getWorkspaceIdentifier(firstWorkspace);
                }
            }

            if (workspaceIdForUrl) {
                urlContextParametersService.setWorkspaceId(workspaceIdForUrl);
            } else {
                const alert: Alert = {
                    key: 'grading.workspace.all.notAvailable',
                    info: 'grading.workspace.all.notAvailable.description',
                    level: AlertLevel.ERROR,
                    payload: {},
                };
                yield put(pushAlert(alert));
            }
        }
    } catch (error) {
        yield put(workspaceSchemeError());
        console.error(error);
    }
}

function* handleUpdatePartyQuestionsRevisionIds(action: any) {
    const contextState: ContextState = yield select(getContextState);
    const { partyState, questionId } = contextState;
    const { questionsList } = partyState;
    let newContextQuestionId: number;
    // if there is no current question selected then no need to do anything
    if (!isNullOrUndefined(questionsList) && questionsList.length) {
        const updatedQuestionsList = questionsList.map((question: HitData) => {
            const updatedQuestionInfo = action.payload.find((updatedQuestion: any) => updatedQuestion.oldRevisionId === question.questionId);
            if (updatedQuestionInfo) {
                if (question.questionId === questionId) {
                    newContextQuestionId = updatedQuestionInfo.revisionId;
                }
                return Object.assign(question, { questionId: updatedQuestionInfo.revisionId });
            }
            return question;
        });
        yield put(setPartyState({ questionsList: updatedQuestionsList }));
        if (!isNullOrUndefined(newContextQuestionId) && newContextQuestionId > 0) {
            urlContextParametersService.setQuestionId(newContextQuestionId);
        }
    }
}

function* callLoadQuestionsForCurrentParty(assessmentRunId: number, partyId: number, questionId: number) {
    yield put(setPartyState({ questionsList: [] }));

    const questionFilters: SearchFilters = { assessmentRunId, partyId };
    let questionInfo: SearchQuery = commonSearchQueryBuilder.createQueryWithFilterAndSort(
        questionFilters,
        'questionNumber',
        'asc',
    );
    questionInfo = commonSearchQueryBuilder.appendSource(questionInfo, 'context');
    const questionSearchJSON: string = encodeURIComponent(JSON.stringify(questionInfo));
    const dataQuestion = yield call(commonApiService.fetchListLiveData, USERQUESTION_INDEX, questionSearchJSON);

    yield put(setPartyState({ questionsList: dataQuestion?.hits }));
    if (dataQuestion && dataQuestion.hits.length > 0) {
        const isValidQuestionId = dataQuestion.hits.some((question: any) => question.questionId === questionId);
        if (!isValidQuestionId && partyId > 0) {
            yield urlContextParametersService.setQuestionId(dataQuestion.hits[0].questionId);
        }
    } else {
        // reset state
        yield put(setQuestionState({
            documentContentRevisionId: -1,
            questionType: '',
            questionNumber: -1,
            questionNumbering: '',
            questionName: '',
            answered: false,
            score: undefined,
            evaluatorScores: undefined,
            maxScore: undefined,
            questionWeight: 1,
            rubricType: undefined,
            stimulusContentRevisionId: -1,
            hasStimulusUserResponse: false,
            noQuestionFound: true,
            hasMaximaValidationIssue: false,
        }));
        urlContextParametersService.setQuestionId(-1);
    }
}

function* getGradingStatusAndActions() {
    const session: SessionState = yield select(getSessionState);
    if (session.user.isStudent) {
        return;
    }
    const context = yield select(getContextState);
    const { assessmentRunId, selectedCommitteeId } = context;
    const controller = new AbortController();
    try {
        const response = yield call(
            apiService.getGradingStatusAndActions,
            assessmentRunId,
            selectedCommitteeId > 0 ? selectedCommitteeId : undefined,
            controller.signal,
        );
        yield put(setGradingWorkflowInfo(response));
    } catch (error) {
        yield put({ error, type: 'context/GET_GRADING_STATUS_AND_ACTIONS_FAILED' });
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* checkLocationAndContext(action: LocationChangeAction) {
    const contextState: ContextState = yield select(getContextState);
    const routerState: RouterState = yield select(getRouterState);
    const newContext: ContextUrlSearchStringObject = routingService.getNewContextFromLocation(routerState.location);
    const { state } = action.payload.location;
    const silentUpdate = state && state.silent;

    // doing this before replaceContext so that the questions list is loaded
    // when context is replaced and it triggers call to the PermissionService.isQuestionIdValidForCurrentCandidate,
    // until we remove replaceContext this must remain here
    if (newContext.partyId > 0 && !isNullOrUndefined(newContext.questionId) && newContext.questionId > 0
        // changed party id or loaded a question when there was no questionId previously
        && (contextState.partyId !== newContext.partyId || contextState.questionId === -1)) {
        const sessionState: SessionState = yield select(getSessionState);
        // the session state isn't loaded when it reaches this point the first time,
        // so we need the window property set from the servlet,
        // though we still leave the session check as an enforcer after the session state is loaded
        const isStudent = sessionState.user.isStudent || window.isStudent;
        // students only have access to the list question api if the marks report is enabled
        if (!isStudent) {
            yield call(
                callLoadQuestionsForCurrentParty,
                newContext.assessmentRunId,
                newContext.partyId,
                newContext.questionId,
            );
        }
    }

    if (isContextNotInSyncWithUrl(contextState, newContext)) {
        yield put(replaceContext(newContext));
    }

    if ((contextState.assessmentRunId !== newContext.assessmentRunId) && newContext.assessmentRunId) {
        yield put(assessmentRunIdUpdated());
    }
    if ((contextState.workspaceId !== newContext.workspaceId) && newContext.workspaceId) {
        yield put(workspaceIdUpdated());
        const enteringMarkingWorkspace = newContext.workspaceId === workspaceID.PLAN_MARKING
            || newContext.workspaceId === workspaceID.EVALUATE_MARKING;
        if (enteringMarkingWorkspace) {
            const fullState: RootState = yield select();
            const gradingPartyDocument = (fullState as any).context?.partyState?.gradingPartyDocument;
            if (shouldOpenAiIntro(fullState, newContext.workspaceId)
                && !isAiIntroKnownReadOnly(gradingPartyDocument)) {
                yield put(openAiSupportedMarkingIntro(
                    newContext.workspaceId,
                    getLoggedInUserId(),
                    newContext.assessmentRunId,
                ));
            }
        }
    }

    if (!silentUpdate) {
        if ((contextState.partyId !== newContext.partyId) && newContext.partyId > 0) {
            if (contextState?.questionState?.noQuestionFound) {
                // new party, try to load question if found it will also reset noQuestionFound
                yield put(updateQuestionState());
            }
            yield put(partyIdUpdated());
        }
    }

    if ((contextState.questionId !== newContext.questionId) && newContext.questionId > 0) {
        yield put(questionIdUpdated());
    }

    if (contextState.selectedCommitteeId !== newContext.selectedCommitteeId && newContext.selectedCommitteeId) {
        yield put(committeeIdSelected());
    }
}

function* callFetchAssessmentRunSessionInfo(action?: any) {
    const contextState: ContextState = yield select(getContext);
    const controller = new AbortController();
    const arSessionId = action?.payload;

    try {
        const { assessmentRunId } = contextState;
        const res: any = yield call(apiService.getAssessmentRunSession, assessmentRunId, arSessionId);
        const {
            success,
            assessmentRunSessionId,
            status,
            contextObjectId,
            contextObjectStartTime,
            contributors,
            workflowKey,
            resource,
            startTime,
            serverLocalTime,
        } = res;

        if (success) {
            // For initial load check
            if (isNullOrUndefined(assessmentRunSessionId)) {
                routingService.redirectToAdminPortal();
            }

            yield put(assessmentRunSessionLoaded({
                assessmentRunSessionId,
                status,
                contextObjectId,
                contextObjectStartTime,
                contributors,
                workflowKey,
                serverLocalTime,
                resource: JSON.parse(resource),
                startTime,
            }));

            // After test finished check, this has to stay on here in order to keep the session loaded, otherwise will break the features
            if (status === AssessmentRunSessionStatus.CLOSED) {
                routingService.redirectToAdminPortal();
            }

            if (workflowKey) {
                const workspacesState: WorkspacesState = yield select(getWorkspaceState);
                const workspaceIdForWorkflowKey = getWorkspaceIdByWorkflowKey(workspacesState.workspaceScheme, workflowKey);
                if (!isNullOrUndefined(workspaceIdForWorkflowKey) && contextState.workspaceId !== workspaceIdForWorkflowKey) {
                    navigationListService.navigateToWorkspace(workspaceIdForWorkflowKey);
                }
            }
            if (status !== AssessmentRunSessionStatus.CLOSED) {
                yield put(refreshHeartbeat());
            }
        }
    } catch (error) {
        yield put({ error, type: 'FETCH_ASSESSMENT_RUN_SESSION_FAILED' });
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* watchRefreshMissingProperties() {
    yield fork(function* refreshMissingProperties() {
        let lastAction: RefreshPropertiesType = null;
        while (true) {
            const action: RefreshPropertiesType = yield take(ContextTypeKeys.REFRESH_MISSING_PROPERTIES);
            const { id, missingProperties } = action.payload;
            if (!lastAction
                || !missingProperties.every((property) => lastAction.payload.missingProperties.includes(property))
                || lastAction.payload.id !== id) {
                lastAction = action;
                yield fork(updateMissingProperties, action);
            }
        }
    });
}

function* checkUrlAndContext() {
    const workspaceState: WorkspacesState = yield select(getWorkspaceState);
    const contextState: ContextState = yield select(getContextState);
    const { featuresLoading, currentRequiredContextProperties } = workspaceState;

    // IA3-1419 Load question info and partyInfo for header
    if (featuresLoading.length === 0) {
        if (contextState.partyId > -1 && !currentRequiredContextProperties.includes('partyState')) {
            yield setPartyStateData();
        }
        if (contextState.questionId > -1 && !currentRequiredContextProperties.includes('questionState')) {
            yield put(updateQuestionState());
        }
    }
}

function* onCommitteeSelectionChanged() {
    const workspaces: WorkspacesState = yield select(getWorkspaceState);
    const { workspaceScheme } = workspaces;

    if (workspaceScheme) {
        const visibleWorkspaces = getVisibleWorkspaces(workspaces);

        yield put(workspaceSchemeLoaded({
            ...workspaceScheme,
            workspaceGroups: visibleWorkspaces,
        }));
    }
}

function* callFetchSpeakingQuestionSetIds() {
    const controller = new AbortController();
    const context: ContextState = yield select((state: RootState) => state.context);
    const { assessmentRunId, assessmentRunSession } = context;
    const candidate = getArSessionCandidateContributor(assessmentRunSession);

    try {
        const speakingQuestions: any = yield call(apiService.fetchQuestionSet,
            assessmentRunId,
            Context.PREVIEW,
            controller.signal,
            candidate?.userId);
        const speakingTaskQuestionIds = parseSpeakingQuestionSetIds(speakingQuestions);

        yield put(speakingQuestionSetIdsLoaded(speakingTaskQuestionIds));
    } catch (error) {
        yield put({ type: 'context/LOAD_SPEAKING_QUESTION_SET_IDS_FAILED', error });
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* closeDetailsPanel() {
    yield put(toggleDetailsPanel());
}

function* onEnactedRecording() {
    yield put(openExplanationRecordingPanelDone());
}

function* handleRefreshHeartbeat() {
    const controller = new AbortController();
    const assessment: AssessmentState = yield select(getAssessmentState);
    const context: ContextState = yield select(getContext);
    const { assessmentRunId, assessmentRunSession } = context;
    if (!assessment.videoBasedOralExam
        || isNullOrUndefined(assessmentRunSession)
        || assessmentRunSession.status === AssessmentRunSessionStatus.CLOSED) {
        return;
    }
    try {
        yield call(apiService.refreshHeartbeat, assessmentRunId, controller.signal);
        // calling every minute
        yield call(delay, 60 * 1000);
        yield put(refreshHeartbeat());
    } catch (error) {
        // in case of error, try again,
        // but delaying 30 seconds until the next try
        yield call(delay, 30 * 1000);
        yield put(refreshHeartbeat());
    } finally {
        if (yield cancelled()) {
            controller.abort();
        }
    }
}

function* handleSubmissionDeletedUpdate(action: any) {
    const context: ContextState = yield select(getContext);
    const { partyId } = context;
    if (partyId === action.payload) {
        yield setPartyStateData(null, true);
        yield put(showReload({
            titleKey: 'grading.reload.submissionDeleted.title',
            messageKey: 'grading.reload.submissionDeleted.message',
        }));
    }
}

/** ************************
    Start of watchers
 ************************ */
function* watchAssessmentRunSession() {
    yield takeLatest(ContextTypeKeys.LOAD_ASSESSMENT_RUN_SESSION, callFetchAssessmentRunSessionInfo);
}

function* watchContextChanges() {
    yield takeLatest(
        [ContextTypeKeys.WORKSPACE_ID_UPDATED, ContextTypeKeys.PARTY_ID_UPDATED,
            ContextTypeKeys.QUESTION_ID_UPDATED],
        syncQueryParametersWithState
    );
}

function* watchRouterLocationChange() {
    yield takeLatest(LOCATION_CHANGE, checkLocationAndContext);
}

function* watchWorkspacesContextChanged() {
    yield takeLatest([ContextTypeKeys.SETUP_WORKSPACES,
        ContextTypeKeys.WORKSPACE_ID_UPDATED,
        ContextTypeKeys.COMMITTEE_ID_SELECTED], loadWorkspaceScheme);
}

function* watchFetchMissingProperties() {
    yield takeEvery(ContextTypeKeys.LOAD_MISSING_PROPERTIES, loadMissingProperties);
}

function* watchGetGradingStatusAndActions() {
    yield takeLatest([ContextTypeKeys.GET_GRADING_WORKFLOW_INFO, ContextTypeKeys.COMMITTEE_ID_SELECTED],
        getGradingStatusAndActions);
}

function* watchAllFeaturesLoadComplete() {
    yield takeLatest(WorkspaceTypeKeys.STOP_FEATURE_LOADING, checkUrlAndContext);
}

function* watchCloseDetailsPanel() {
    yield takeLatest(DetailsPanelTypeKeys.CLOSE_DETAILS_PANEL, closeDetailsPanel);
}

function* watchEnactedRecording() {
    yield takeLatest(RecordingTypeKeys.ENACT_RECORDING, onEnactedRecording);
}

function* watchCommitteeSelectionChanged() {
    yield takeLatest(ContextTypeKeys.COMMITTEE_ID_SELECTED, onCommitteeSelectionChanged);
}

function* watchUpdatePartyQuestionsRevisionIds() {
    yield takeLatest(ContextTypeKeys.UPDATE_CONTEXT_PARTY_QUESTION_REVISION_IDS, handleUpdatePartyQuestionsRevisionIds);
}

function* watchRefreshHeartbeat() {
    yield takeLatest(ContextTypeKeys.SPEAKING_REFRESH_HEARTBEAT, handleRefreshHeartbeat);
}

function* watchSubmissionDeleted() {
    yield takeLatest(ContextTypeKeys.SUBMISSION_DELETED, handleSubmissionDeletedUpdate);
}

function* watchSetQuestionData() {
    yield takeLatest([AssessmentKeys.USER_QUESTION_INDEX_UPDATE, ContextTypeKeys.UPDATE_QUESTION_STATE], setQuestionStateData);
}

export function* rootContextSaga() {
    yield all([
        watchRouterLocationChange(),
        watchFetchMissingProperties(),
        watchRefreshMissingProperties(),
        watchWorkspacesContextChanged(),
        watchContextChanges(),
        watchGetGradingStatusAndActions(),
        watchAllFeaturesLoadComplete(),
        watchCloseDetailsPanel(),
        watchEnactedRecording(),
        watchCommitteeSelectionChanged(),
        watchUpdatePartyQuestionsRevisionIds(),
        watchAssessmentRunSession(),
        watchRefreshHeartbeat(),
        watchSubmissionDeleted(),
        watchSetQuestionData(),
    ]);
}
